import { StrictMode } from 'react';

import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import 'symbol-observable';

import App from './App';

if (env.Sentry.dsn !== null) {
    Sentry.init({
        dsn: env.Sentry.dsn,
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: 0,
        environment: env.NODE_ENV,

        // Stratégie de filtrage :
        // 1. ignoreErrors → bruit navigateur résiduel connu
        // 2. beforeSend → ne garde que les erreurs avec au moins une frame first-party
        //    (remplace allowUrls/denyUrls qui laissaient passer les erreurs sans stacktrace)

        ignoreErrors: [
            'Non-Error promise rejection captured', // https://github.com/getsentry/sentry-javascript/issues/3440
            'ResizeObserver loop completed with undelivered notifications', // Limitation navigateur, pas un bug
            'Java object is gone', // Bridge Android WebView détruit (Instagram/Facebook in-app)
            /window\.webkit\.messageHandlers/, // Bridge iOS WebView (Facebook/Safari in-app)
            "Can't find variable: wpmobileappStopLoading", // WordPress mobile app
            'window.Beacon is not a function', // Help Scout Beacon non chargé (extension/race condition)
            /^window\.Beacon/, // Help Scout Beacon — scripts tiers
            "Can't find variable: gtag", // Google tag non chargé (bloqueur de pub)
            '__SCRIPT_SOURCE_PLACEHOLDER__', // Extension navigateur injectant des placeholders
            'The object does not support the operation or argument.', // InvalidAccessError navigateur ancien
            'Should not already be working.', // React internals (concurrent mode), pas actionnable
            // Erreurs réseau client — pas actionnables côté serveur
            'Network Error', // Axios
            'Failed to fetch', // Chrome (équivalent du Load failed Safari)
            'TypeError: cancelled', // Safari — fetch annulé par navigation
            // Lazy-loading échoue (deploy pendant navigation ou réseau instable)
            /Loading chunk .+ failed/i,
            /Loading CSS chunk .+ failed/i,
            'ChunkLoadError',
        ],

        beforeSend(event, hint) {
            const error = hint?.originalException;

            // Google Translate modifie le DOM → React ne peut plus réconcilier (React #11538)
            if (
                error instanceof DOMException &&
                /removeChild|insertBefore|appendChild/.test(error.message)
            ) {
                return null;
            }

            // Safari iOS : fetch interrompu quand l'utilisateur quitte la page
            if (error instanceof TypeError && error.message === 'Load failed') {
                return null;
            }

            // Seules les erreurs avec au moins une frame dans notre code sont envoyées.
            // Élimine : bridges natifs (Android/iOS/WordPress), extensions, scripts tiers.
            const frames =
                event.exception?.values?.flatMap(
                    (v) => v.stacktrace?.frames ?? [],
                ) ?? [];
            const hasFirstPartyFrame = frames.some(
                (f) =>
                    f.filename != null &&
                    f.filename !== '' &&
                    /yurplan\.com|yp\.events|ypl\.me|\/yurplan-frontend\//.test(
                        f.filename,
                    ),
            );
            if (!hasFirstPartyFrame) {
                return null;
            }

            return event;
        },
    });
}

const container = document.querySelector('#app');
if (container === null) {
    throw new Error('No container found');
}
const appName = container.getAttribute('data-app-name');
if (appName === null) {
    throw new Error('No application found');
}
const root = createRoot(container);
root.render(
    <StrictMode>
        <App appName={appName as Configuration.AppName} />
    </StrictMode>,
);
